import { useState, useEffect, useRef } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameDay,
  isBefore,
  isAfter,
  parse,
  startOfDay,
  parseISO,
} from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import TaskPlanner from "../pages/admin/ArtistManager/TaskPlanner/TaskPlanner";

export default function Calendar({ mode, eventsData, onChange, showAdd = true }) {
  const [view, setView] = useState("calendar");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [currentMonth, setCurrentMonth] = useState(
    new Date(selectedYear, selectedMonth)
  );
  // With this — persist selection in a module-level ref so it survives modal close/reopen:
  const persistedRange = useRef({ startDate: null, endDate: null });
  const [startDate, setStartDate] = useState(() => persistedRange.current.startDate);
  const [endDate, setEndDate] = useState(() => persistedRange.current.endDate);
  // const [startDate, setStartDate] = useState(null);
  // const [endDate, setEndDate] = useState(null);
  const [addTask, setAddTask] = useState(false);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const years = Array.from({ length: 11 }, (_, i) => 2025 + i);

  // Default events/tasks derived from TaskPlanner fields
  const [events, setEvents] = useState(eventsData || []);

  // Helper: normalize date to midnight in LOCAL timezone
  // This function extracts ONLY the date part and creates a new Date at local midnight
  // IMPORTANT: If the input is an ISO string, interpret the date part in UTC (use UTC getters)
  // so that an ISO like "2025-11-12T23:59:59.000Z" maps to the 12th (not 13th in local timezones).
  const normalizeDate = (d) => {
    if (!d) return null;

    let dateObj;

    if (typeof d === "string") {
      // Try ISO parse first
      dateObj = parseISO(d);
      if (isNaN(dateObj.getTime())) {
        // fallback to Date constructor
        dateObj = new Date(d);
      }
      if (isNaN(dateObj.getTime())) {
        // last resort: try common format MM/dd/yyyy
        dateObj = parse(d, "MM/dd/yyyy", new Date());
      }

      if (isNaN(dateObj.getTime())) return null;

      // Extract UTC components from the parsed ISO/string value
      // This ensures we interpret the date *as written* in UTC rather than shifted to local.
      const year = dateObj.getUTCFullYear();
      const month = dateObj.getUTCMonth();
      const day = dateObj.getUTCDate();

      // Return a Date at local midnight for that UTC date (so calendar comparisons use local-midnight)
      return new Date(year, month, day, 0, 0, 0, 0);
    } else if (d instanceof Date) {
      // If a Date object was passed, assume it already represents the intended local date/time.
      // Normalize to local midnight by using getFullYear/getMonth/getDate (local getters).
      if (isNaN(d.getTime())) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    }

    return null;
  };

  // Sync events with eventsData prop (API updates)
  useEffect(() => {
    // Convert all event start/end to Date objects at midnight
    const normalized = (eventsData || []).map(ev => {
      const startNormalized = normalizeDate(ev.start || ev.start_date);
      let endNormalized = normalizeDate(ev.end || ev.end_date);

      // If end_date is null or invalid (like 1970-01-01), use start date
      if (!endNormalized || endNormalized.getFullYear() === 1970) {
        endNormalized = startNormalized;
      }

      return {
        ...ev,
        start: startNormalized,
        end: endNormalized,
      };
    });
    setEvents(normalized);
  }, [eventsData]);

  const calendarRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [tooltipTaskIndex, setTooltipTaskIndex] = useState(0);
  const [activeDate, setActiveDate] = useState(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (mode === "year") setView("yearList");
    else if (mode === "month") setView("monthList");
    else setView("calendar");
  }, [mode]);

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setView("monthList");
  };

  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setCurrentMonth(new Date(selectedYear, monthIndex));
    setView("calendar");
  };

  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const startOfCurrentMonth = startOfMonth(currentMonth);
  const endOfCurrentMonth = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({
    start: startOfCurrentMonth,
    end: endOfCurrentMonth,
  });

  // today's date — used to highlight today's day only when viewing the current month
  const today = startOfDay(new Date());
  const isViewingCurrentMonth =
    today.getFullYear() === currentMonth.getFullYear() &&
    today.getMonth() === currentMonth.getMonth();

  const handleDateClick = (date) => {
    const normalizedDate = startOfDay(date);

    if (!startDate || (startDate && endDate)) {
      setStartDate(normalizedDate);
      setEndDate(null);
      // notify parent about single selection
      if (typeof onChange === "function") onChange({ startDate: normalizedDate, endDate: null });
    } else if (isBefore(normalizedDate, startDate)) {
      setStartDate(normalizedDate);
      if (typeof onChange === "function") onChange({ startDate: normalizedDate, endDate });
    } else {
      setEndDate(normalizedDate);
      if (typeof onChange === "function") onChange({ startDate, endDate: normalizedDate });
    }
    // toggle active tooltip on click
    setActiveDate((prev) => (prev && isSameDay(prev, normalizedDate) ? null : normalizedDate));
  };

  // Replace the existing handleDateClick function:
  // const handleDateClick = (date) => {
  //   const normalizedDate = startOfDay(date);

  //   if (!startDate || (startDate && endDate)) {
  //     persistedRange.current = { startDate: normalizedDate, endDate: null };
  //     setStartDate(normalizedDate);
  //     setEndDate(null);
  //     if (typeof onChange === "function") onChange({ startDate: normalizedDate, endDate: null });
  //   } else if (isBefore(normalizedDate, startDate)) {
  //     persistedRange.current = { startDate: normalizedDate, endDate };
  //     setStartDate(normalizedDate);
  //     if (typeof onChange === "function") onChange({ startDate: normalizedDate, endDate });
  //   } else {
  //     persistedRange.current = { startDate, endDate: normalizedDate };
  //     setEndDate(normalizedDate);
  //     if (typeof onChange === "function") onChange({ startDate, endDate: normalizedDate });
  //   }
  //   setActiveDate((prev) => (prev && isSameDay(prev, normalizedDate) ? null : normalizedDate));
  // };

  const isInRange = (date) => {
    if (!startDate || !endDate) return false;
    const d = startOfDay(date);
    return isAfter(d, startDate) && isBefore(d, endDate);
  };

  const eventsForDate = (date) => {
    // Normalize date to midnight for comparison
    const d = startOfDay(date);
    return events.filter((ev) => {
      const s = ev.start;
      const e = ev.end;
      if (!s || !e) return false;

      // Inclusive range: date is same as start, same as end, or between
      return (isSameDay(d, s) || isSameDay(d, e) || (isAfter(d, s) && isBefore(d, e)));
    });
  };

  const monthHasEvents = (year, monthIndex) => {
    return events.some((ev) => {
      const s = ev.start;
      const e = ev.end || ev.start;
      if (!s) return false;

      // if event range intersects this month
      const monthStart = new Date(year, monthIndex, 1, 0, 0, 0, 0);
      const monthEnd = endOfMonth(monthStart);
      return !(isAfter(s, monthEnd) || isBefore(e, monthStart));
    });
  };

  const yearHasEvents = (year) => {
    return events.some((ev) => {
      const s = ev.start;
      const e = ev.end || ev.start;
      if (!s) return false;

      return s.getFullYear() === year || e.getFullYear() === year || (s.getFullYear() < year && e.getFullYear() > year);
    });
  };

  const showTooltipFor = (date, e) => {
    const evs = eventsForDate(date);
    setTooltipTaskIndex(0);
    // clear any pending hide timer
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const wrapperRect = calendarRef.current?.getBoundingClientRect();
    const rect = e.currentTarget.getBoundingClientRect();

    // compute tooltip width based on calendar width (leave small margin)
    const maxTooltipWidth = Math.min(360, (wrapperRect?.width || 320) - 24);
    const tooltipWidth = Math.max(200, Math.min(320, maxTooltipWidth));

    // horizontally center over the date cell using translateX(-50%) to avoid manual width math
    const wrapperWidth = Math.round(wrapperRect?.width || 320);
    const centerX = Math.round(rect.left - (wrapperRect?.left || 0) + rect.width / 2);
    const halfTooltip = Math.round(tooltipWidth / 2);
    const minLeft = 8;
    const maxLeft = wrapperWidth - 8;
    const minCenter = minLeft + halfTooltip;
    const maxCenter = Math.max(minCenter, maxLeft - halfTooltip);
    // clamp center to ensure tooltip won't overflow
    const clampedCenter = Math.round(Math.min(Math.max(centerX, minCenter), maxCenter));
    // we'll position at clampedCenter and use translateX(-50%) in CSS to center horizontally
    const left = clampedCenter;

    // estimate tooltip height based on number of events (approx lines) so we can place above if space allows
    const estimatedLineHeight = 18; // px per line approx
    const estimatedTitleHeight = 20; // date/title area
    const estimatedEventHeight = evs.length * (estimatedLineHeight * 2); // title + desc per event
    const estimatedTooltipHeight = Math.min(220, estimatedTitleHeight + estimatedEventHeight + 12);

    // preferred top above the cell; if not enough space, show below
    const spaceAbove = rect.top - (wrapperRect?.top || 0);
    let top;
    if (spaceAbove > estimatedTooltipHeight + 12) {
      // show above, with small gap
      top = Math.round(rect.top - (wrapperRect?.top || 0) - estimatedTooltipHeight - 8);
    } else {
      // show below cell
      top = Math.round(rect.top - (wrapperRect?.top || 0) + rect.height + 8);
    }

    setTooltip({ date, events: evs, left, top, width: tooltipWidth, transform: 'translateX(-50%)', showAdd: evs.length === 0 });
  };

  const hideTooltip = () => {
    // delay hiding slightly to allow moving pointer into tooltip
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setTooltip(null);
      hideTimerRef.current = null;
    }, 150);
  };

  // Modal state for editing an event via TaskPlanner
  const [editingEvent, setEditingEvent] = useState(null);

  const openEditorFor = (ev) => {
    setEditingEvent(ev);
    // keep tooltip open while editor is visible
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const closeEditor = () => {
    setEditingEvent(null);
  };

  const saveEditedEvent = (payload) => {
    setEvents((prev) => prev.map((it) => (it.id === editingEvent.id ? { ...it, ...payload } : it)));
    closeEditor();
    setTooltip(null);
  };

  // clear timer on unmount
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const fadeSlide = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
  };

  return (
    <div className="bg-white rounded-xl flex items-center justify-center flex-1 p-4 overflow-visible relative">
      <AnimatePresence mode="wait">
        {view === "yearList" && (
          <motion.div
            key="yearList"
            variants={fadeSlide}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="w-full text-center"
          >
            <h4 className="text-theme_clr1 font-bold mb-3">Select Year</h4>
            <div className="grid grid-cols-3 gap-2">
              {years.map((year) => (
                <div
                  key={year}
                  onClick={() => handleYearSelect(year)}
                  className={`cursor-pointer py-2 rounded-lg font-medium transition ${selectedYear === year
                    ? "bg-theme_clr1 text-white"
                    : yearHasEvents(year)
                      ? "bg-orange-400 text-theme_clr1 font-semibold"
                      : "bg-theme_clr2 text-white hover:bg-theme_clr1"
                    }`}
                >
                  {year}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {view === "monthList" && (
          <motion.div
            key="monthList"
            variants={fadeSlide}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="w-full text-center"
          >
            <h4 className="text-theme_clr1 font-bold mb-3">
              {mode === "year" ? `Select Month for ${selectedYear}` : "Select Month"}
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {months.map((month, index) => (
                <div
                  key={month}
                  onClick={() => handleMonthSelect(index)}
                  className={`cursor-pointer py-2 rounded-lg font-medium transition ${selectedMonth === index
                    ? "bg-theme_clr1 text-white"
                    : monthHasEvents(selectedYear, index)
                      ? "bg-orange-400 text-theme_clr1 font-semibold"
                      : "bg-theme_clr2 text-white hover:bg-theme_clr1"
                    }`}
                >
                  {month.slice(0, 3)}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {view === "calendar" && (
          <motion.div
            key="calendar"
            variants={fadeSlide}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="calendar-wrapper max-w-[312px] w-full text-center mx-auto"
          >
            {(startDate && endDate) && (
              <div className="text-theme_clr1 text-[14.25px] font-medium mb-4">
                {format(startDate, "dd/MM/yyyy")} - {format(endDate, "dd/MM/yyyy")}
              </div>
            )}

            <div className="text-theme_clr1 text-[12.8px] font-roboto font-medium flex items-center justify-between mb-2">
              <button className="h-[30px] w-[40px]" onClick={goToPreviousMonth}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <span
                className="cursor-pointer font-semibold"
                onClick={() => setView("monthList")}
              >
                {format(currentMonth, "MMMM yyyy")}
              </span>
              <button className="h-[30px] w-[40px]" onClick={goToNextMonth}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>

            <div ref={calendarRef} className="font-roboto grid grid-cols-7 gap-[1px] text-xs text-theme_clr1 text-center relative">
              {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
                <div
                  key={index}
                  className="h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center"
                >
                  {day}
                </div>
              ))}

              {Array.from({ length: startOfCurrentMonth.getDay() }).map((_, i) => (
                <div key={i} className="p-2"></div>
              ))}

              {daysInMonth.map((date) => {
                const normalizedDate = startOfDay(date);
                const isStart = startDate && isSameDay(normalizedDate, startDate);
                const isEnd = endDate && isSameDay(normalizedDate, endDate);
                const inRange = isInRange(normalizedDate);
                const isToday = isViewingCurrentMonth && isSameDay(normalizedDate, today);
                const evs = eventsForDate(normalizedDate);

                return (
                  <div
                    key={date.toString()}
                    onClick={() => handleDateClick(date)}
                    onMouseEnter={(e) => showTooltipFor(normalizedDate, e)}
                    onMouseLeave={hideTooltip}
                    className={`text-sm font-inter h-[35px] w-[35px] md:h-[40px] md:w-[40px] flex items-center justify-center cursor-pointer transition 
                      ${isStart || isEnd
                        ? "bg-gradient-to-r from-indigo-400 to-indigo-900 text-white font-semibold rounded-full"
                        : inRange
                          ? "bg-gradient-to-r from-indigo-300 to-indigo-800 text-white"
                          : isToday
                            ? "bg-theme_clr1 text-white font-semibold rounded-full"
                            : "text-theme_clr1 hover:bg-gray-200 rounded-full"
                      }`}
                    style={{
                      borderRadius:
                        isStart || isEnd
                          ? "50%"
                          : inRange
                            ? "9999px"
                            : "50%",
                    }}
                  >
                    <div className="relative w-full h-full flex items-center justify-center">
                      <span>{format(date, "d")}</span>
                      {evs.length > 0 && (() => {
                        // push the dots down slightly when the date has a circular highlight
                        const hasCircle = isStart || isEnd || isToday;
                        const dotBottom = hasCircle ? -10 : 4; // px
                        return (
                          <span className="absolute left-1/2 -translate-x-1/2 flex gap-[2px]" style={{ bottom: dotBottom }}>
                            {Array.from({ length: Math.min(evs.length, 5) }).map((_, i) => (
                              <span key={i} className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                            ))}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
              {(tooltip && showAdd) && (
                <div
                  className="absolute z-50 border border-gray-200 rounded-lg shadow-lg text-left text-xs text-theme_clr1"
                  style={{
                    left: tooltip.left,
                    top: tooltip.top,
                    transform: tooltip.transform || undefined,
                    minWidth: '200px',
                    maxWidth: tooltip.width,
                    backgroundColor: 'rgba(255,255,255,0.92)',
                    padding: '5px'
                  }}
                  onMouseEnter={() => {
                    if (hideTimerRef.current) {
                      clearTimeout(hideTimerRef.current);
                      hideTimerRef.current = null;
                    }
                  }}
                  onMouseLeave={hideTooltip}
                >
                  <div className="font-semibold text-center mb-1">{format(tooltip.date, "dd MMM yyyy")}</div>
                  <div className="flex flex-col gap-1">
                    {tooltip.events.length > 0 ? (
                      <div className="flex items-center justify-between gap-2">
                        <button
                          className={`px-2 py-1 text-theme_clr1 ${tooltipTaskIndex === 0 ? 'opacity-30 cursor-default' : ''}`}
                          disabled={tooltipTaskIndex === 0}
                          onClick={() => setTooltipTaskIndex(i => Math.max(0, i - 1))}
                        >
                          <i className="bi bi-chevron-left" />
                        </button>
                        <div className="flex-1">
                          {(() => {
                            const ev = tooltip.events[tooltipTaskIndex];
                            return (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 cursor-pointer" onClick={() => openEditorFor(ev)}>
                                  <div className="font-semibold leading-tight">{ev.title || ev.objective} <span className="text-[11px] text-gray-500">• {ev.category}</span></div>
                                  <div className="text-[12px] leading-tight">{ev.description || ev.objective}</div>
                                  {ev.start && ev.end && (
                                    <div className="text-[11px] text-gray-500">{format(ev.start, "dd MMM")} → {format(ev.end, "dd MMM")}</div>
                                  )}
                                  {ev.notes && <div className="text-[11px] text-gray-600">{ev.notes}</div>}
                                </div>
                                <button
                                  className="ml-2 px-2 py-1 bg-theme_clr1 text-white rounded-full text-xs font-semibold"
                                  onClick={() => openEditorFor(ev)}
                                >Edit</button>
                                {ev.completed && (
                                  <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500">
                                    <i className="bi bi-check text-white text-xs" />
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <button
                          className={`px-2 py-1 text-theme_clr1 ${tooltipTaskIndex === tooltip.events.length - 1 ? 'opacity-30 cursor-default' : ''}`}
                          disabled={tooltipTaskIndex === tooltip.events.length - 1}
                          onClick={() => setTooltipTaskIndex(i => Math.min(tooltip.events.length - 1, i + 1))}
                        >
                          <i className="bi bi-chevron-right" />
                        </button>
                      </div>
                    ) : (
                      <button className="bg-theme_clr1 text-white rounded px-3 py-1 mt-2 text-xs font-semibold" onClick={() => setAddTask(true)}>
                        Add Task
                      </button>
                    )}
                  </div>
                </div>
              )}
              {editingEvent && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
                  <div className="bg-theme_clr1 rounded-lg p-4 w-[min(560px,90%)]">
                    <h3 className="text-white text-lg font-semibold mb-2">Edit Task</h3>
                    <TaskPlanner
                      initialData={editingEvent}
                      onSubmit={saveEditedEvent}
                      onCancel={closeEditor}
                    />
                  </div>
                </div>
              )}
              {addTask && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
                  <div className="bg-theme_clr1 rounded-lg p-4 w-[min(580px,90%)]">
                    <TaskPlanner
                      initialData={{}}
                      forAdd
                      onCancel={() => setAddTask(false)}
                    />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
